import { JSX, useEffect, useMemo, useState } from "react";
import * as Separator from "@radix-ui/react-separator";
import { PriceSlider } from "~/app/tickets/[eventId]/_components/Filter/PriceSlider";
import {
  getMaxPriceValue,
  getMinPriceValue,
} from "~/app/tickets/[eventId]/_components/helpers";
import { useTranslations } from "~/hooks/useTranslations";
import { usePricesSearchParams } from "~/app/tickets/[eventId]/_components/Filter/hooks";
import { PlainMessage } from "@bufbuild/protobuf";
import { Section } from "@egocentric-systems/ts-apis/booking_gateway/v1/events_pb";
import { useLegend } from "~/hooks/useLegend";
import { cn, moneyToCurrency } from "~/lib/utils";

interface IFilterContent {
  sections?: Array<PlainMessage<Section>>;
  setModalOpen: (value: boolean) => void;
}

export function FilterContent({
  sections,
  setModalOpen,
}: IFilterContent): JSX.Element {
  const t = useTranslations();
  const [{ min, max, category }, setSearchParams, clearSearchParams] =
    usePricesSearchParams();
  const [priceRange, setPriceRange] = useState<[number, number]>();

  const categories = useLegend(sections ?? []);

  const [selectedCategory, setSelectedCategory] = useState(category);

  const maxPrice = useMemo(() => {
    if (!sections) return 0;

    return sections.reduce((maxValue, section) => {
      const sectionMaxPrice = getMaxPriceValue(section.prices);

      return Math.round(Math.max(maxValue, 1 + sectionMaxPrice));
    }, 0);
  }, [sections]);

  const minPrice = useMemo(() => {
    if (!sections) return 0;

    return sections.reduce((minValue, section) => {
      const sectionMinPrice = getMinPriceValue(section.prices);

      return Math.round(Math.min(minValue, Math.max(0, -1 + sectionMinPrice)));
    }, Infinity);
  }, [sections]);

  useEffect(() => {
    setPriceRange([
      Number(min !== null ? min : minPrice),
      Number(max !== null ? max : maxPrice),
    ]);
  }, [min, max, minPrice, maxPrice]);

  const onClick = () => {
    setSearchParams({
      min: priceRange?.[0].toString(),
      max: priceRange?.[1].toString(),
      category: selectedCategory ?? "",
    });
    setModalOpen(false);
  };

  return (
    <>
      <fieldset className="px-[12px]">
        <Separator.Root className="my-4 bg-[#00000026] data-[orientation=horizontal]:h-px data-[orientation=vertical]:h-full data-[orientation=horizontal]:w-full data-[orientation=vertical]:w-px" />
        <div className="my-[10px] text-[16px] font-bold">{t.ticket_price}</div>
        <PriceSlider
          minPrice={minPrice}
          maxPrice={maxPrice}
          onChangePrice={setPriceRange}
          priceRange={priceRange ?? [0, 0]}
        />
      </fieldset>
      <fieldset className="px-[12px]">
        <Separator.Root className="my-4 bg-[#00000026] data-[orientation=horizontal]:h-px data-[orientation=vertical]:h-full data-[orientation=horizontal]:w-full data-[orientation=vertical]:w-px" />
        <div className="my-[10px] text-[16px] font-bold">
          {t.seatmap_legend_categories}
        </div>
        <ul className="flex flex-col gap-2">
          {categories.map((c) => (
            <li
              key={c.priceGoupId}
              className={cn(
                "flex cursor-pointer flex-col gap-2 rounded-lg border border-gray-300 p-2 transition-colors",
                selectedCategory === c.priceGoupId && "bg-gray-200",
              )}
              onClick={() => {
                setSelectedCategory((currentCategory) =>
                  c.priceGoupId === currentCategory ? null : c.priceGoupId,
                );
              }}
            >
              <div className="flex items-center gap-6">
                <span
                  className="flex aspect-square h-7 items-center justify-center rounded p-1 text-sm"
                  style={{
                    backgroundColor: c.color,
                  }}
                />

                <span className="text-sm text-muted-foreground">
                  {moneyToCurrency(c.maxPrice)}
                </span>
              </div>
            </li>
          ))}
        </ul>
      </fieldset>
      <div className="flex w-full flex-col gap-4 p-[10px]">
        <Separator.Root className="mt-4 bg-[#00000026] data-[orientation=horizontal]:h-px data-[orientation=vertical]:h-full data-[orientation=horizontal]:w-full data-[orientation=vertical]:w-px" />

        <button
          onClick={onClick}
          className="rounded-[8px] bg-black px-[16px] py-[4px] font-medium text-white transition-colors hover:cursor-pointer hover:bg-black/80"
        >
          {t.apply_filter}
        </button>
        <button
          onClick={() => {
            clearSearchParams();
            setModalOpen(false);
          }}
          className="rounded-[8px] border border-[#000000]/15 bg-white px-[16px] py-[4px] font-medium text-black transition-colors hover:cursor-pointer hover:bg-black/5"
        >
          {t.reset}
        </button>
      </div>
    </>
  );
}
